<template>
    <v-row justify="center">
        <v-dialog
            v-model="dialog"
            persistent
            max-width="370">
            <v-card>
                <div class="modal-close-btn">
                    <div class="cross-style" @click="closeModal()">
                        x
                    </div>
                </div>

                <div class="modal-content">
                    <v-card-title class="center pt-0">
                        {{ submitText }}
                    </v-card-title>
                </div>
                <v-card-text>
                    <v-form
                        lazy-validation
                        ref="form"
                        v-model="valid">
                        <v-container>
                            <v-row>
                                <v-col
                                    cols="12">
                                    <v-text-field
                                        :label="$vuetify.lang.t('$vuetify.companyHolidayForm.name')"
                                        v-model="form.name"
                                        placeholder=" "
                                        :rules="inputRules"
                                        :error-messages="errors.name">
                                    </v-text-field>
                                </v-col>
                                <v-col
                                    cols="12">
                                    <v-menu
                                        v-model="datePickerShow"
                                        :close-on-content-click="false"
                                        :nudge-right="40"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="290px" >
                                        <template v-slot:activator="{ on }">
                                            <v-text-field
                                                v-model="dateFormatted"
                                                :label="$vuetify.lang.t('$vuetify.companyHolidayForm.date')"
                                                placeholder=" "
                                                :rules="inputRules"
                                                append-icon="event"
                                                :error-messages="errors.date"
                                                @click:append="datePickerShow = true"
                                                readonly
                                                v-on="on"
                                            ></v-text-field>
                                        </template>
                                        <v-date-picker
                                            :locale="localeLang"
                                            v-model="form.date"
                                            :min="minDate"
                                            @change="checkDate"
                                            @input="datePickerShow = false">
                                        </v-date-picker>
                                    </v-menu>
                                </v-col>
                                <v-col
                                    cols="12">
                                    <v-textarea
                                        :label="$vuetify.lang.t('$vuetify.companyHolidayForm.description')"
                                        placeholder=" "
                                        :error-messages="errors.description"
                                        v-model="form.description"
                                        counter
                                        rows="2"
                                        no-resize
                                        :rules="descriptionRules"
                                        :maxlength="255">
                                    </v-textarea>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-form>
                    <div class="modal-save-btn">
                        <v-btn
                            width="200"
                            color="primary"
                            @click="submit()">
                            {{ $vuetify.lang.t('$vuetify.companyHolidayForm.save') }}
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import { mask } from 'vue-the-mask'
export default {
    name: 'CompanyHolidayForm',

    props: {
        mode: {
            type: String,
            default: null
        },

        updateCompanyHolidayId: {
            type: Number,
            default: null
        }
    },

    directives: {
        mask
    },

    computed: {
        localeLang () {
            return this.$vuetify.lang.current
        },
        submitText () {
            if (this.mode === 'create') {
                return this.$vuetify.lang.t('$vuetify.companyHolidayForm.createCompanyHoliday')
            }
            return this.$vuetify.lang.t('$vuetify.companyHolidayForm.updateCompanyHoliday')
        }
    },

    data () {
        return {
            dialog: false,
            valid: true,
            form: {
                name: '',
                date: '',
                description: ''
            },
            dateFormatted: '',
            inputRules: [
                v => this.$validator.hasValue(v, this.trans.companyHolidayForm.thisFieldIsRequired),
                v => this.$validator.lengthNotExceed(v, 255, this.trans.companyHolidayForm.thisFieldIsExceededCharacters)
            ],
            descriptionRules: [
                v => this.$validator.lengthNotExceed(v, 255, this.trans.companyHolidayForm.thisFieldIsExceededCharacters)
            ],
            datePickerShow: false,
            errors: {},
            minDate: null
        }
    },

    methods: {
        openModal () {
            this.dialog = true
        },

        closeModal () {
            this.$refs.form.resetValidation()
            this.dialog = false
            this.errors = {}
            this.datePickerShow = false
            this.clearForm()
            this.$emit('modal-closed')
        },

        clearForm () {
            this.form = {
                name: '',
                date: '',
                description: ''
            }
        },

        getFormData () {
            let form = {}
            if (this.mode === 'create') {
                form = { ...this.form }
            } else {
                const { date, name, description } = this.form
                form = { date, name, description }
            }

            form.name = form.name.trim()
            form.description = form.description.trim()

            return form
        },

        async createCompanyHoliday () {
            this.$store.dispatch('setLoading', true)
            const res = await this.axios.post('company/holiday/create', this.getFormData())
            if (res && res.data && res.data.status === 'error_inputs') {
                this.errors = res.data.errors
                this.$store.dispatch('setLoading', false)
            }
            if (res && res.data && res.data.status === 'ok') {
                this.$emit('success-created')
                this.dialog = false
                this.closeModal()
                this.$store.dispatch('setLoading', false)

                this.$notify({
                    title: this.trans.companyHolidayForm.congratulation,
                    text: this.trans.companyHolidayForm.companyHolidayIsCreated,
                    type: 'success'
                })
            }
        },

        async getCompanyHoliday (id) {
            this.$store.dispatch('setLoading', true)
            const res = await this.axios.get(`company/holiday/${id}`)
            if (res && res.data && res.data.status === 'ok') {
                this.form = res.data.data
                this.$store.dispatch('setLoading', false)
            }
        },

        async updateCompanyHoliday () {
            this.$store.dispatch('setLoading', true)

            const res = await this.axios.put(
                `company/holiday/edit/${this.updateCompanyHolidayId}`,
                this.getFormData()
            )
            if (res && res.data && res.data.status === 'error_inputs') {
                this.errors = res.data.errors
                this.$store.dispatch('setLoading', false)

                for (const property in this.errors) {
                    this.$notify({
                        title: this.errorMsg,
                        text: this.errors[property],
                        type: 'error'
                    })
                }
            }
            if (res && res.data && res.data.status === 'ok') {
                this.$emit('success-updated')
                this.dialog = false
                this.closeModal()
                this.$store.dispatch('setLoading', false)

                this.$notify({
                    title: this.trans.companyHolidayForm.congratulation,
                    text: this.trans.companyHolidayForm.companyHolidayIsUpdated,
                    type: 'success'
                })
            }
        },

        async submit () {
            if (!this.$refs.form.validate() || this.errors.date) {
                return
            }
            if (this.mode === 'create') {
                await this.createCompanyHoliday()
            } else if (this.mode === 'update') {
                await this.updateCompanyHoliday()
            }
        },

        async checkDate () {
            let form = null
            if (this.mode === 'create') {
                form = {
                    date: this.form.date
                }
            } else if (this.mode === 'update') {
                form = {
                    date: this.form.date,
                    id: this.updateCompanyHolidayId
                }
            }

            const res = await this.axios.post('company/holiday/check_date', form)
            if (res && res.data && res.data.status === 'error_inputs') {
                this.errors = res.data.errors
                this.$store.dispatch('setLoading', false)
            } else {
                this.errors.date = null
            }
        },

        setMinDate () {
            const now = new Date()
            const nextDate = now.setDate(now.getDate() + 1)
            return new Date(nextDate).toISOString().substr(0, 10)
        }

    },

    mounted () {
        this.minDate = this.setMinDate()
        this.registerLang({
            companyHolidayForm: [
                'thisFieldIsRequired',
                'thisFieldIsExceededCharacters',
                'congratulation',
                'companyHolidayIsCreated',
                'companyHolidayIsUpdated'
            ]
        })
    },

    watch: {
        async mode (newValue, oldValue) {
            if (newValue === 'create') {
                this.clearForm()
            } else if (newValue === 'update') {
                await this.getCompanyHoliday(this.updateCompanyHolidayId)
            }
        },
        'form.date' () {
            this.dateFormatted = this.$util.formatDate(this.form.date)
        },
        '$vuetify.lang.current': {
            handler: function (after, before) {
                if (this.dialog) {
                    this.$refs.form.resetValidation()
                }
            },
            deep: true
        }
    }
}
</script>

<style lang="scss" scoped>

</style>
